import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, Mail, Folder, Users } from "lucide-react";
import { toast } from "sonner";

const BUNDLES = [
  {
    key: "drive",
    label: "Google Drive",
    icon: Folder,
    description: "Upload of generated invoices, freelance contracts and CRA exports to BAIM Drive folders.",
  },
  {
    key: "gmail",
    label: "Gmail",
    icon: Mail,
    description: "Send invoice emails, Weekly CRA submissions and CRA export emails on your behalf.",
  },
  {
    key: "directory",
    label: "Workspace Directory",
    icon: Users,
    description: "List BAIM Workspace users (CEO/Admin only — used for auto-provisioning).",
  },
];

export default function AccountConnections() {
  const { user } = useAuth();
  const [scopes, setScopes] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/me/scopes");
      setScopes(r.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load scopes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const elevateBundle = (bundle) => {
    const next = encodeURIComponent("/account/connections");
    window.location.href = `${API}/auth/google/elevate?scope=${encodeURIComponent(bundle)}&next=${next}`;
  };
  const elevateAll = () => {
    const next = encodeURIComponent("/account/connections");
    window.location.href = `${API}/auth/google/elevate?scope=all&next=${next}`;
  };

  if (!user) return null;
  const anyMissing = scopes && (!scopes.drive || !scopes.gmail || !scopes.directory);

  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="account-connections-page">
      <div className="space-y-2">
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Account · Connections</div>
        <h1 className="font-heading font-bold text-3xl text-zinc-900">Google account permissions</h1>
        <p className="text-sm text-zinc-600 max-w-2xl leading-relaxed">
          Each capability below requires a separate Google scope. Re-link individually if a single integration is failing,
          or use <strong>Re-link all</strong> for a one-shot consent that covers every bundle.
        </p>
      </div>

      {/* Account header --------------------------------------------------- */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 flex items-center gap-4" data-testid="account-header">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-sm font-medium uppercase">
            {(user.name || user.email || "?").slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-zinc-900 truncate">{user.name}</div>
          <div className="text-xs text-zinc-500 truncate font-mono">{user.email}</div>
        </div>
        <button
          onClick={elevateAll}
          data-testid="relink-all-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
        >
          <ShieldCheck size={14} /> Re-link all
        </button>
      </div>

      {/* Banner ----------------------------------------------------------- */}
      {anyMissing ? (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 flex items-start gap-3" data-testid="missing-banner">
          <AlertTriangle size={18} className="text-orange-600 mt-0.5" />
          <div className="text-sm text-orange-900">
            <div className="font-medium">At least one Google permission is missing.</div>
            <div className="text-xs text-orange-800 mt-1">
              Re-link the affected bundle below — Google will display the consent screen for that scope only.
            </div>
          </div>
        </div>
      ) : !loading && scopes && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 flex items-start gap-3" data-testid="all-granted-banner">
          <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <div className="font-medium">All Google permissions are granted.</div>
            <div className="text-xs text-emerald-800 mt-1">
              You can still re-link if Google revoked a token — it's safe and idempotent.
            </div>
          </div>
        </div>
      )}

      {/* Per-bundle list -------------------------------------------------- */}
      <div className="space-y-3" data-testid="bundles-list">
        {BUNDLES.map(({ key, label, icon: Icon, description }) => {
          const granted = !!scopes?.[key];
          return (
            <div
              key={key}
              data-testid={`bundle-row-${key}`}
              className={`bg-white border rounded-lg p-5 flex items-center gap-4 ${
                granted ? "border-emerald-300" : "border-orange-300"
              }`}
            >
              <div className={`w-11 h-11 rounded-md flex items-center justify-center ${granted ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>
                <Icon size={20} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-zinc-900">{label}</span>
                  {granted ? (
                    <span data-testid={`status-${key}`} className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-700">
                      <CheckCircle2 size={12} /> Granted
                    </span>
                  ) : (
                    <span data-testid={`status-${key}`} className="inline-flex items-center gap-1 text-[11px] font-mono text-orange-700">
                      <AlertTriangle size={12} /> Missing
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{description}</div>
              </div>
              <button
                onClick={() => elevateBundle(key)}
                data-testid={`relink-${key}-btn`}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-3 h-9 text-xs font-medium rounded-md whitespace-nowrap ${
                  granted
                    ? "border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                    : "bg-orange-600 text-white hover:bg-orange-700"
                }`}
              >
                <RefreshCw size={12} /> {granted ? "Re-link" : "Grant"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Granted scopes (raw, debug-friendly) ----------------------------- */}
      {scopes?.scopes?.length ? (
        <details className="bg-zinc-50 border border-zinc-200 rounded-lg p-4" data-testid="raw-scopes-details">
          <summary className="text-xs text-zinc-700 font-medium cursor-pointer">Granted scopes (raw)</summary>
          <ul className="mt-3 space-y-1">
            {scopes.scopes.map((s) => (
              <li key={s} className="text-[11px] font-mono text-zinc-600 break-all">{s}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
